// pages/Support.js
import { MessageSquare, Paperclip, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useAuth } from "../context/AuthContext";
import { useConfig } from "../context/ConfigContext"; // Import useConfig hook

const Support = () => {
  const { user } = useAuth();
  const { config } = useConfig(); // Get config from context
  const [formData, setFormData] = useState({
    name: user ? `${user.firstName} ${user.lastName}` : "",
    email: user ? user.email : "",
    subject: "",
    category: "order",
    message: "",
    attachments: [],
  });
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    try {
      // Set the document title using the site name from config
      document.title = "Support | " + config?.siteName;
    } catch (err) {
      console.error("Error fetching page:", err);
    }
  }, [config?.siteName]); // Add config.siteName to the dependency array

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      // 5MB limit
      setErrorMessage("File size must be less than 5MB");
      e.target.value = null;
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch("/api/tickets/attachment", {
        method: "POST",
        headers: user
          ? {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          }
          : undefined,
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload file");
      }

      const result = await response.json();

      setFormData((prev) => ({
        ...prev,
        attachments: [...prev.attachments, result.fileUrl],
      }));

      setErrorMessage("");
      e.target.value = null;
    } catch (error) {
      setErrorMessage(error.message || "Error uploading file");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setErrorMessage("Name is required");
      return;
    }

    if (!formData.email.trim()) {
      setErrorMessage("Email is required");
      return;
    }

    if (!formData.subject.trim()) {
      setErrorMessage("Subject is required");
      return;
    }

    if (!formData.message.trim()) {
      setErrorMessage("Message is required");
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");

      const ticketData = {
        subject: formData.subject,
        category: formData.category,
        message: formData.message,
        name: formData.name,
        email: formData.email,
        attachments: formData.attachments,
      };

      const headers = {
        "Content-Type": "application/json",
      };

      // Add auth token if user is logged in
      if (user) {
        headers["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
      }

      const response = await fetch("/api/tickets", {
        method: "POST",
        headers,
        body: JSON.stringify(ticketData),
      });

      if (!response.ok) {
        throw new Error("Failed to submit support request");
      }

      const result = await response.json();

      setSuccessMessage(
        "Your support request has been submitted successfully. We will respond to you as soon as possible."
      );
      setSubmitted(true);

      // Reset form
      setFormData({
        name: user ? `${user.firstName} ${user.lastName}` : "",
        email: user ? user.email : "",
        subject: "",
        category: "order",
        message: "",
        attachments: [],
      });
    } catch (error) {
      setErrorMessage(error.message || "Error submitting support request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className=" max-w-4xl mx-auto px-4 py-24">
      <Helmet>
        <title>{"Support | " + config.siteName}</title>
        <meta
          name="description"
          content={"Need any support? We are here to help you with any request"}
        />
      </Helmet>

      <div className="text-center mb-12">
        <h1
          className="text-3xl font-bold mb-4"
          style={{ color: config.colors.textPrimary }}
        >
          Customer Support
        </h1>
        <p
          className="max-w-2xl mx-auto"
          style={{ color: config.colors.textSecondary }}
        >
          Need help? Our support team is here to assist you. Please fill out the
          form below and we'll get back to you as soon as possible.
        </p>
      </div>

      {submitted && successMessage ? (
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${config.colors.secondary}20` }}
          >
            <MessageSquare
              className="h-8 w-8"
              style={{ color: config.colors.secondary }}
            />
          </div>
          <h2
            className="text-2xl font-bold mb-4"
            style={{ color: config.colors.textPrimary }}
          >
            Thank You!
          </h2>
          <p className="mb-6" style={{ color: config.colors.textSecondary }}>
            {successMessage}
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="px-4 py-2 text-white rounded-md transition-colors"
            style={{
              backgroundColor: config.colors.primary,
              ":hover": { backgroundColor: `${config.colors.primary}DD` },
            }}
          >
            Submit Another Request
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div
            className="p-6 border-b"
            style={{
              backgroundColor: `${config.colors.primary}10`,
              borderColor: `${config.colors.primary}30`,
            }}
          >
            <h2
              className="text-xl font-semibold"
              style={{ color: config.colors.primary }}
            >
              Submit a Support Request
            </h2>
            <p className="mt-1" style={{ color: config.colors.primary }}>
              Fill out the form below with your question or issue
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {errorMessage && (
              <div
                className="mb-6 p-4 border-l-4 text-red-700"
                style={{
                  backgroundColor: "#FEF2F2",
                  borderColor: "#EF4444",
                }}
              >
                {errorMessage}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium mb-1"
                  style={{ color: config.colors.textPrimary }}
                >
                  Your Name*
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none transition-colors"
                  style={{
                    borderColor: config.colors.inputBorder,
                    ":focus": {
                      borderColor: config.colors.primary,
                      boxShadow: `0 0 0 2px ${config.colors.primary}30`,
                    },
                  }}
                  placeholder="John Doe"
                  disabled={user !== null}
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-1"
                  style={{ color: config.colors.textPrimary }}
                >
                  Email Address*
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none transition-colors"
                  style={{
                    borderColor: config.colors.inputBorder,
                    ":focus": {
                      borderColor: config.colors.primary,
                      boxShadow: `0 0 0 2px ${config.colors.primary}30`,
                    },
                  }}
                  placeholder="your@email.com"
                  disabled={user !== null}
                  required
                />
              </div>
            </div>

            <div className="mb-6">
              <label
                htmlFor="subject"
                className="block text-sm font-medium mb-1"
                style={{ color: config.colors.textPrimary }}
              >
                Subject*
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md focus:outline-none transition-colors"
                style={{
                  borderColor: config.colors.inputBorder,
                  ":focus": {
                    borderColor: config.colors.primary,
                    boxShadow: `0 0 0 2px ${config.colors.primary}30`,
                  },
                }}
                placeholder="Brief description of your issue"
                required
              />
            </div>

            <div className="mb-6">
              <label
                htmlFor="category"
                className="block text-sm font-medium mb-1"
                style={{ color: config.colors.textPrimary }}
              >
                Category*
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md focus:outline-none transition-colors"
                style={{
                  borderColor: config.colors.inputBorder,
                  ":focus": {
                    borderColor: config.colors.primary,
                    boxShadow: `0 0 0 2px ${config.colors.primary}30`,
                  },
                }}
                required
              >
                <option value="order">Order Issue</option>
                <option value="product">Product Information</option>
                <option value="shipping">Shipping & Delivery</option>
                <option value="payment">Payment Issue</option>
                <option value="account">Account Help</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="mb-6">
              <label
                htmlFor="message"
                className="block text-sm font-medium mb-1"
                style={{ color: config.colors.textPrimary }}
              >
                Message*
              </label>
              <textarea
                id="message"
                name="message"
                rows="6"
                value={formData.message}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md focus:outline-none transition-colors"
                style={{
                  borderColor: config.colors.inputBorder,
                  ":focus": {
                    borderColor: config.colors.primary,
                    boxShadow: `0 0 0 2px ${config.colors.primary}30`,
                  },
                }}
                placeholder="Please describe your issue in detail"
                required
              ></textarea>
            </div>

            <div className="mb-6">
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: config.colors.textPrimary }}
              >
                Attachments
              </label>

              {formData.attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.attachments.map((url, index) => (
                    <div
                      key={index}
                      className="flex items-center px-3 py-1 rounded-md"
                      style={{ backgroundColor: config.colors.backgroundAlt }}
                    >
                      <Paperclip
                        className="h-4 w-4 mr-1"
                        style={{ color: config.colors.textSecondary }}
                      />
                      <span
                        className="text-sm"
                        style={{ color: config.colors.textPrimary }}
                      >
                        Attachment {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            attachments: formData.attachments.filter(
                              (_, i) => i !== index
                            ),
                          })
                        }
                        className="ml-2 hover:text-red-700"
                        style={{ color: "#EF4444" }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center">
                <label className="block">
                  <span className="sr-only">Choose file</span>
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    disabled={loading}
                    className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:hover:bg-blue-100"
                    style={{
                      color: config.colors.textSecondary,
                      "--tw-file-bg": `${config.colors.primary}10`,
                      "--tw-file-text": config.colors.primary,
                    }}
                  />
                </label>
              </div>

              <p
                className="text-xs mt-1"
                style={{ color: config.colors.textSecondary }}
              >
                Max file size: 5MB. Allowed formats: images, PDF, documents
              </p>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center px-6 py-3 text-white rounded-md transition-colors disabled:opacity-50"
                style={{
                  backgroundColor: config.colors.primary,
                  ":hover": { backgroundColor: `${config.colors.primary}DD` },
                }}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Request
                  </>
                )}
              </button>
            </div>
          </form>

          <div
            className="p-6 border-t"
            style={{
              backgroundColor: config.colors.backgroundAlt,
              borderColor: `${config.colors.textSecondary}20`,
            }}
          >
            <h3
              className="text-lg font-medium mb-2"
              style={{ color: config.colors.textPrimary }}
            >
              Alternative Ways to Reach Us
            </h3>
            <p className="mb-4" style={{ color: config.colors.textSecondary }}>
              You can also contact our support team directly:
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <div
                className="p-4 bg-white border rounded-lg flex-1"
                style={{ borderColor: `${config.colors.textSecondary}20` }}
              >
                <h4
                  className="font-medium"
                  style={{ color: config.colors.textPrimary }}
                >
                  Email Support
                </h4>
                <p className="text-sm mt-1">
                  <a
                    href={`mailto:${config.contactEmail || "support@pitech.ca"
                      }`}
                    className="hover:underline"
                    style={{ color: config.colors.primary }}
                  >
                    {config.contactEmail || "support@pitech.ca"}
                  </a>
                </p>
              </div>
              <div
                className="p-4 bg-white border rounded-lg flex-1"
                style={{ borderColor: `${config.colors.textSecondary}20` }}
              >
                <h4
                  className="font-medium"
                  style={{ color: config.colors.textPrimary }}
                >
                  Phone Support
                </h4>
                <p className="text-sm mt-1">
                  <a
                    href={`tel:${config.contactPhone || "+18888088869"}`}
                    className="hover:underline"
                    style={{ color: config.colors.primary }}
                  >
                    {config.contactPhone || "+1 (888) 808-8869"}
                  </a>
                </p>
                <p
                  className="text-xs mt-1"
                  style={{ color: config.colors.textSecondary }}
                >
                  Mon-Fri: 9AM - 5PM PT
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
